"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ModeStateConfigGenerator = exports.IControlSignalClassDefinitionSchema = exports.ControlSignalClassSchema = exports.ControlSignalClass = void 0;
const zod_1 = require("zod");
const BoundaryLevel_1 = require("../BoundaryLevel");
const IModeStateConfig_1 = require("../DesignModels/IModeStateConfig");
const ModeStateType_1 = require("../DesignModels/ModeStateType");
const ModeSignalLogicalStateCondition_1 = require("../DesignModels/ModeSignalLogicalStateCondition");
const SignalDirection_1 = require("../DesignModels/SignalDirection");
const SignalDriveMode_1 = require("../DesignModels/SignalDriveMode");
var ControlSignalClass;
(function (ControlSignalClass) {
    ControlSignalClass["Reset"] = "reset";
    ControlSignalClass["Enable"] = "enable";
    ControlSignalClass["Uvlo"] = "uvlo";
})(ControlSignalClass || (exports.ControlSignalClass = ControlSignalClass = {}));
exports.ControlSignalClassSchema = zod_1.z.nativeEnum(ControlSignalClass);
exports.IControlSignalClassDefinitionSchema = zod_1.z.object({
    classId: exports.ControlSignalClassSchema,
    aliases: zod_1.z.set(zod_1.z.string()),
    stateConfig: IModeStateConfig_1.IModeStateConfigSchema,
});
class ModeStateConfigGenerator {
    static { this.ID_PREFIX = 'gen-signal-logic'; }
    static { this.RESET_STATE_CONFIG = {
        states: [
            { stateId: ModeStateType_1.ModeStateType.LogicLow, isNominalState: true, unreachableSeverity: BoundaryLevel_1.BoundaryLevel.Error },
            { stateId: ModeStateType_1.ModeStateType.LogicHigh, unreachableSeverity: BoundaryLevel_1.BoundaryLevel.Warning },
        ],
    }; }
    static { this.ENABLE_LIKE_STATE_CONFIG = {
        states: [
            { stateId: ModeStateType_1.ModeStateType.LogicHigh, isNominalState: true, unreachableSeverity: BoundaryLevel_1.BoundaryLevel.Error },
            { stateId: ModeStateType_1.ModeStateType.LogicLow },
        ],
    }; }
    static { this.CLASS_DEFINITIONS = [
        {
            classId: ControlSignalClass.Reset,
            aliases: new Set(['RST', 'RESET', 'RESETB', 'RSTB']),
            stateConfig: ModeStateConfigGenerator.RESET_STATE_CONFIG,
        },
        {
            classId: ControlSignalClass.Enable,
            aliases: new Set(['EN', 'ENA', 'ENABLE']),
            stateConfig: ModeStateConfigGenerator.ENABLE_LIKE_STATE_CONFIG,
        },
        {
            classId: ControlSignalClass.Uvlo,
            aliases: new Set(['UVLO', 'UVIN', 'VUVLO', 'UV']),
            stateConfig: ModeStateConfigGenerator.ENABLE_LIKE_STATE_CONFIG,
        },
    ]; }
    static normalizeControlSignalToken(name) {
        let token = name.trim().toUpperCase();
        token = token.replace(/^[/!~#\\]+/, '');
        token = token.replace(/[#/]+$/, '');
        token = token.replace(/[_-](?:N|B|L)$/, '');
        const segments = token.split(/[-_/.]+/).filter(Boolean);
        if (segments.length > 0) {
            token = segments[segments.length - 1];
        }
        token = token.replace(/[^A-Z0-9]/g, '');
        token = token.replace(/\d+$/, '');
        if (/^N[A-Z]{2,}$/.test(token)) {
            token = token.slice(1);
        }
        return token;
    }
    static classifyControlSignal(signalName) {
        return ModeStateConfigGenerator.findClassDefinition(signalName)?.classId;
    }
    static generateModeStateForSignal(signal, existingModes) {
        if (!ModeStateConfigGenerator.isControlInputCandidate(signal)) {
            return undefined;
        }
        const classDefinition = ModeStateConfigGenerator.findClassDefinition(signal.name);
        if (!classDefinition) {
            return undefined;
        }
        const alreadyDerivedFromSignal = existingModes.some((mode) => (mode.preconditions ?? []).some((precondition) => (0, ModeSignalLogicalStateCondition_1.isModeSignalLogicalStateCondition)(precondition.condition) &&
            String(precondition.condition.signalId) === String(signal.id)));
        if (alreadyDerivedFromSignal) {
            return undefined;
        }
        const id = ModeStateConfigGenerator.buildGeneratedModeId(classDefinition.classId, signal);
        if (existingModes.some((mode) => mode.id === id)) {
            return undefined;
        }
        return {
            id,
            preconditions: [{ id: 1, condition: { signalId: signal.id } }],
            stateConfig: ModeStateConfigGenerator.copyStateConfig(classDefinition.stateConfig),
        };
    }
    static generateModeStatesForComponent(component) {
        const generated = [];
        const knownModes = [
            ...component.modes,
            ...(component.model?.inferenceReport?.modes?.map((entry) => entry.mode) ?? []),
        ];
        for (const signal of component.signals) {
            const mode = ModeStateConfigGenerator.generateModeStateForSignal(signal, knownModes);
            if (mode) {
                generated.push(mode);
                knownModes.push(mode);
            }
        }
        return generated;
    }
    static isControlInputCandidate(signal) {
        if (signal.signalDirection !== SignalDirection_1.SignalDirection.Input) {
            return false;
        }
        if (signal.attributes?.digital !== true) {
            return false;
        }
        const driveModes = signal.supportedDriveModes ?? [];
        if (driveModes.length === 0) {
            return false;
        }
        if (!driveModes.every((driveMode) => driveMode.type === SignalDriveMode_1.SignalInputDriveType.HighZ)) {
            return false;
        }
        return driveModes.some((driveMode) => driveMode.parameters?.inputHighVoltage !== undefined && driveMode.parameters?.inputLowVoltage !== undefined);
    }
    static findClassDefinition(signalName) {
        const token = ModeStateConfigGenerator.normalizeControlSignalToken(signalName);
        return ModeStateConfigGenerator.CLASS_DEFINITIONS.find((definition) => definition.aliases.has(token));
    }
    static buildGeneratedModeId(classId, signal) {
        return `${ModeStateConfigGenerator.ID_PREFIX}:${classId}:${signal.id}`;
    }
    static copyStateConfig(template) {
        return { states: template.states.map((state) => ({ ...state })) };
    }
}
exports.ModeStateConfigGenerator = ModeStateConfigGenerator;
//# sourceMappingURL=ModeStateConfigGenerator.js.map